import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

const toDateValue = (date) => {
    if (!(date instanceof Date)) return '';
    const iso = date.toISOString();
    return iso.slice(0, 10); // yyyy-MM-dd
};

const toStartOfDayIso = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
};

const toEndOfDayIso = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    // move to start of next day to make the upper bound inclusive for the selected date
    d.setUTCDate(d.getUTCDate() + 1);
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
};

export const useRequests = () => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [from, setFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return toDateValue(d);
    });
    const [to, setTo] = useState(() => toDateValue(new Date()));

    const fetchRequests = async () => {
        if (!user?.token) return;
        try {
            setLoading(true);
            const params = new URLSearchParams();
            const fromIso = toStartOfDayIso(from);
            const toIso = toEndOfDayIso(to);
            if (fromIso) params.append('from', fromIso);
            if (toIso) params.append('to', toIso);
            const qs = params.toString();
            const url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/requests${qs ? `?${qs}` : ''}`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'تعذر جلب الطلبات');
            }
            setRequests(Array.isArray(json.requests) ? json.requests : []);
            if (selectedRequestId) {
                const exists = (json.requests || []).some((r) => String(r._id) === String(selectedRequestId));
                if (!exists) setSelectedRequestId(null);
            }
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedRequest = useMemo(
        () => requests.find((r) => String(r._id) === String(selectedRequestId)) || null,
        [requests, selectedRequestId]
    );

    return {
        requests,
        loading,
        selectedRequest,
        selectedRequestId,
        setSelectedRequestId,
        from,
        to,
        setFrom,
        setTo,
        fetchRequests,
    };
};
